import React, {useCallback, useEffect, useState} from 'react';
import {IFarmInputInvoice, ISupplierUserProfile} from 'constants/interfaces';
import {apiGetDepotOrder} from 'constants/Api';
import {EOrderState} from 'constants/index';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';
import {AppBar, Box, Grid, Paper, Toolbar, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {ProductState} from 'appredux/reducers/reducer-products';
import {OrderDetail} from 'screens/Dashboard/Orders/OrderDetail/OrderDetail';
import {InvoiceDetail} from 'screens/Dashboard/Invoices/InvoiceDetail/InvoiceDetail';

import './OrderDetail.scss';

export const OrderDetailPage = (props: {
  user: ISupplierUserProfile;
  orderId: number;
  productState: ProductState;
  width: Breakpoint;
}) => {
  const {user, orderId, productState, width} = props;

  const [invoice, setInvoice] = useState<IFarmInputInvoice | undefined>();
  const [state, setState] = useState<EOrderState | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback((id: number) => {
    setIsLoading(true);
    setLoadError(undefined);
    apiGetDepotOrder(id)
      .then((invoice) => {
        console.log(invoice);
        setInvoice(invoice);
        setState(invoice.state);
      })
      .catch((error) => {
        setLoadError('Error loading order. Please try again later.');
        console.error(error);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    load(orderId);
  }, [orderId, load]);

  if (isLoading && !invoice) {
    return <OrderDetailPageSkeleton />;
  }

  if (loadError || !invoice || state === undefined) {
    return (
      <div className="order-detail" style={{padding: 24, textAlign: 'center'}}>
        <Typography color="error">{loadError || 'Order not found.'}</Typography>
      </div>
    );
  }

  if (state === EOrderState.PROFORMA) {
    return (
      <OrderDetail
        user={user}
        invoiceId={orderId}
        productState={productState}
        width={width}
        initialInvoice={invoice}
        onConvertedToInvoice={() => load(orderId)}
      />
    );
  }

  return (
    <InvoiceDetail
      user={user}
      invoiceId={orderId}
      productState={productState}
      width={width}
      initialInvoice={invoice}
    />
  );
};

const OrderDetailPageSkeleton = () => {
  return (
    <div className="order-detail">
      <AppBar className="app-bar" elevation={0}>
        <Toolbar>
          <Skeleton variant="circle" width={32} height={32} />
          <Box ml={2} flex={1}>
            <Skeleton variant="text" width={180} height={28} />
          </Box>
          <Skeleton variant="circle" width={32} height={32} />
        </Toolbar>
      </AppBar>

      <Grid container className="container">
        <Grid
          item
          xs={12}
          md={8}
          className="grid-column order-detail-details-column"
        >
          <Paper className="invoice-info-container" variant="outlined">
            {['Order Details', 'Farm Details', 'Line Items', 'Logistics'].map(
              (title, index) => (
                <Box
                  className={
                    title === 'Line Items'
                      ? 'order-products-container-2'
                      : 'farm-details-container'
                  }
                  key={title}
                >
                  <Box className="header-container">
                    <Typography variant="subtitle1">{title}</Typography>
                  </Box>
                  <Skeleton
                    variant="rect"
                    height={index === 2 ? 180 : 160}
                    style={{borderRadius: 8}}
                  />
                </Box>
              ),
            )}
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          className="grid-column order-detail-chat-column"
        >
          <Skeleton variant="rect" height="100%" style={{borderRadius: 8}} />
        </Grid>
      </Grid>
    </div>
  );
};
